import React, { useState, useEffect, useCallback } from 'react';
import { AITestButton } from '../components/shared/AITestButton';
import { listingsApi } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import { ConfirmationModal } from '../components/shared/ConfirmationModal';

interface Listing {
  id: string;
  title: string;
  description: string;
  price: number;
  condition: 'New' | 'Used' | 'Salvage';
  category: string;
  images: string[];
  seller: {
    id: string;
    name: string;
    avatar: string;
    location: string;
  };
  project: {
    id: string;
    name: string;
  } | null;
  createdAt: string;
  isSaved: boolean;
}

export const Marketplace: React.FC = () => {
  console.log('[Marketplace] Component rendering');
  
  const { user } = useAuth();
  const [listings, setListings] = useState<Listing[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'all' | 'saved' | 'mine'>('all');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [listingToDelete, setListingToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    console.log('[Marketplace] useEffect running - fetching listings');
    
    const fetchListings = async () => {
      try {
        setLoading(true);
        console.log('[Marketplace] Calling API to fetch listings');
        const response = await listingsApi.list({ limit: 50 });
        
        if (response.data.success && response.data.data) {
          console.log('[Marketplace] Received', response.data.data.length, 'listings');
          setListings(response.data.data);
        } else {
          console.log('[Marketplace] No listings received');
          setListings([]);
        }
      } catch (error) {
        console.error('[Marketplace] Failed to fetch listings:', error);
        setListings([]);
      } finally {
        setLoading(false);
      }
    };

    fetchListings();
  }, []); // Only run once on mount

  const fetchListings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await listingsApi.list({ limit: 50 });
      
      if (response.data.success && response.data.data) {
        setListings(response.data.data);
      } else {
        setListings([]);
      }
    } catch (error) {
      console.error('Failed to fetch listings:', error);
      setListings([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleSave = async (listingId: string) => {
    try {
      const listing = listings.find(l => l.id === listingId);
      if (!listing) return;

      if (listing.isSaved) {
        await listingsApi.unsave(listingId);
      } else {
        await listingsApi.save(listingId);
      }

      setListings(listings.map(l => 
        l.id === listingId 
          ? { ...l, isSaved: !l.isSaved }
          : l
      ));
    } catch (error) {
      console.error('Failed to save/unsave listing:', error);
    }
  };

  const handleBuy = (listingId: string) => {
    // TODO: Implement buy flow
    console.log('Buy listing:', listingId);
  };

  const handleMessage = (sellerId: string) => {
    // TODO: Open message thread with seller
    console.log('Message seller:', sellerId);
  };

  const handleDeleteListing = (listingId: string) => {
    setListingToDelete(listingId);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!listingToDelete) return;

    setDeleting(true);
    try {
      const response = await listingsApi.delete(listingToDelete);
      if (response.data.success) {
        setListings(listings.filter(l => l.id !== listingToDelete));
      } else {
        throw new Error(response.data.error?.message || 'Failed to delete listing');
      }
    } catch (error: any) {
      console.error('Failed to delete listing:', error);
      alert('Failed to delete listing. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
      setListingToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setListingToDelete(null);
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('en-NZ', {
      style: 'currency',
      currency: 'NZD',
      minimumFractionDigits: 0
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    if (diffInHours < 48) return 'Yesterday';
    return date.toLocaleDateString();
  };

  const filteredListings = viewMode === 'saved' 
    ? listings.filter(listing => listing.isSaved)
    : viewMode === 'mine' && user
    ? listings.filter(listing => listing.seller.id === user.id)
    : listings;

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-4">
        <div className="animate-pulse space-y-6">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card p-6">
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-3/4 mb-4"></div>
              <div className="h-48 bg-slate-200 dark:bg-slate-700 rounded mb-4"></div>
              <div className="h-4 bg-slate-200 dark:bg-slate-700 rounded w-1/2"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-4 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-4">
          {/* AI Test Button - Only visible to admins */}
          <AITestButton 
            onListingsGenerated={fetchListings}
            className="hidden sm:block"
          />
        </div>
        <div className="flex items-center space-x-3">
          {/* Mobile AI Test Button */}
          <AITestButton 
            onListingsGenerated={fetchListings}
            className="sm:hidden"
          />
          {/* View Toggle */}
          <div className="flex bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
            <button
              onClick={() => setViewMode('all')}
              className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'all'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setViewMode('saved')}
              className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'saved'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              Saved
            </button>
            <button
              onClick={() => setViewMode('mine')}
              className={`px-3 py-2 rounded-md text-xs font-medium transition-colors ${
                viewMode === 'mine'
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 shadow-sm'
                  : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              Mine
            </button>
          </div>
          
          <button 
            className="btn-primary p-2"
            title="Create Listing"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        </div>
      </div>

      {/* Search Bar */}
      <div className="mb-6">
        <div className="relative">
          <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <input
            type="search"
            placeholder="Search..."
            className="input pl-12 w-full text-lg py-3"
          />
        </div>
      </div>

      {/* Empty State */}
      {filteredListings.length === 0 && (
        <div className="text-center py-12">
          <div className="w-24 h-24 mx-auto mb-4 bg-slate-100 dark:bg-slate-800 rounded-full flex items-center justify-center">
            <svg className="w-12 h-12 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-slate-900 dark:text-slate-100 mb-2">
            {viewMode === 'saved' ? 'No saved listings' : 'No listings available'}
          </h3>
          <p className="text-slate-600 dark:text-slate-400 mb-6">
            {viewMode === 'saved' 
              ? 'Items you save will appear here for easy access.'
              : 'Be the first to create a listing in the marketplace.'
            }
          </p>
          {viewMode === 'all' && (
            <button className="btn-primary">
              Create First Listing
            </button>
          )}
        </div>
      )}

      {/* Listings Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredListings.map((listing) => (
          <div key={listing.id} className="card p-0 overflow-hidden">
            {/* Image */}
            {listing.images.length > 0 && (
              <div className="relative">
                <img
                  src={listing.images[0]}
                  alt={listing.title}
                  className="w-full h-48 object-cover"
                />
                {listing.images.length > 1 && (
                  <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-sm">
                    +{listing.images.length - 1}
                  </div>
                )}
              </div>
            )}

            <div className="p-4">
              {/* Header */}
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 dark:text-slate-100 mb-1 line-clamp-2">
                    {listing.title}
                  </h3>
                  <div className="flex items-center space-x-2 text-sm text-slate-500">
                    <span className={`px-2 py-1 rounded-full text-xs ${
                      listing.condition === 'New' 
                        ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-300'
                        : listing.condition === 'Used'
                        ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300'
                        : 'bg-orange-100 text-orange-700 dark:bg-orange-900/20 dark:text-orange-300'
                    }`}>
                      {listing.condition}
                    </span>
                    <span className="text-slate-400">•</span>
                    <span>{listing.category}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-xl font-bold text-primary-600 dark:text-primary-400">
                    {formatPrice(listing.price)}
                  </div>
                </div>
              </div>

              {/* Description */}
              <p className="text-slate-600 dark:text-slate-400 text-sm mb-4 line-clamp-2">
                {listing.description}
              </p>

              {/* Seller Info */}
              <div className="flex items-center space-x-2 mb-3">
                <img
                  src={listing.seller.avatar}
                  alt={listing.seller.name}
                  className="w-6 h-6 rounded-full object-cover"
                />
                <span className="text-sm text-slate-600 dark:text-slate-400">
                  {listing.seller.name}
                </span>
                <span className="text-slate-400">•</span>
                <span className="text-sm text-slate-500">
                  {formatDate(listing.createdAt)}
                </span>
              </div>

              {/* Project Link */}
              {listing.project && (
                <div className="mb-4">
                  <button className="text-sm text-primary-600 dark:text-primary-400 hover:underline flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                    From: {listing.project.name}
                  </button>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex items-center space-x-2">
                {user && listing.seller.id === user.id ? (
                  // Owner's buttons
                  <>
                    <button
                      onClick={() => handleDeleteListing(listing.id)}
                      className="flex-1 btn-destructive"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                      Delete
                    </button>
                  </>
                ) : (
                  // Buyer's buttons
                  <>
                    <button
                      onClick={() => handleSave(listing.id)}
                      className={`flex-1 btn-secondary ${
                        listing.isSaved ? 'bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-300' : ''
                      }`}
                    >
                      <svg className={`w-4 h-4 mr-1 ${listing.isSaved ? 'fill-current' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                      {listing.isSaved ? 'Saved' : 'Save'}
                    </button>
                    
                    <button
                      onClick={() => handleMessage(listing.seller.id)}
                      className="flex-1 btn-secondary"
                    >
                      <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                      </svg>
                      Message
                    </button>
                    
                    <button
                      onClick={() => handleBuy(listing.id)}
                      className="flex-1 btn-primary"
                >
                  <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m6-5v6a2 2 0 01-2 2H9a2 2 0 01-2-2v-6m8 0V9a2 2 0 00-2-2H9a2 2 0 00-2 2v4.01" />
                  </svg>
                  Buy
                    </button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={showDeleteConfirm}
        title="Delete Listing"
        message={`Are you sure you want to delete this listing? This action cannot be undone and will permanently remove the listing from the marketplace.`}
        confirmText="Delete Listing"
        cancelText="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isLoading={deleting}
        variant="danger"
      />
    </div>
  );
};
